import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BlockNoteEditor } from '@blocknote/core';
import { BlockNoteView } from '@blocknote/mantine';
import { PdfPreviewModal } from '@features/markdown-editor-ai/components/PdfPreviewModal';
import { usePostV2ProviderDocumentsGeneratePdfMutation } from '@features/markdown-editor-ai/store/api';
import { useProviderBillingInfo } from '@features/user';
import { useUpdateUserPreferencesMutation, useUserPreferences } from '@features/user-preferences';
import {
  Cancel as CancelIcon,
  Save as SaveIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import debounce from 'lodash/debounce';
import { useSnackbar } from 'notistack';

import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';

// Extend UserPreferences to include document_preferences
interface UserPreferencesWithDocument {
  document_preferences?: {
    letterhead_md_string?: string;
  };
  [key: string]: unknown;
}

interface DocumentPreferencesFormProps {
  onSuccessfulFormSubmission?: () => void;
}

const DocumentPreferencesForm: React.FC<DocumentPreferencesFormProps> = ({
  onSuccessfulFormSubmission,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { preferences, isLoading: isLoadingPreferences } = useUserPreferences();
  const providerBillingInfo = useProviderBillingInfo();
  const [updateUserPreferences, { isLoading: isSaving }] = useUpdateUserPreferencesMutation();
  const [generatePdf, { isLoading: isGeneratingPdf }] =
    usePostV2ProviderDocumentsGeneratePdfMutation();

  const [hasChanges, setHasChanges] = useState(false);
  const [localContent, setLocalContent] = useState<string>('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfData, setPdfData] = useState<{ filename: string; pdf_data: string } | null>(null);
  const editorRef = useRef<BlockNoteEditor | null>(null);

  // Generate default letterhead from provider billing info
  const generateDefaultLetterhead = useCallback(() => {
    if (!providerBillingInfo) return '';

    const parts = [];

    // Add practice/provider name
    if (providerBillingInfo.communications_name) {
      parts.push(`**${providerBillingInfo.communications_name}**`);
    } else if (providerBillingInfo.first_name && providerBillingInfo.last_name) {
      parts.push(`**${providerBillingInfo.first_name} ${providerBillingInfo.last_name}**`);
    }

    // Add address
    const addressParts = [];
    if (providerBillingInfo.facility_address) {
      addressParts.push(providerBillingInfo.facility_address);
    }
    if (providerBillingInfo.facility_address2) {
      addressParts.push(providerBillingInfo.facility_address2);
    }
    if (providerBillingInfo.city || providerBillingInfo.state || providerBillingInfo.zip) {
      const cityStateZip = [
        providerBillingInfo.city,
        providerBillingInfo.state,
        providerBillingInfo.zip,
      ]
        .filter(Boolean)
        .join(' ');
      if (cityStateZip) {
        addressParts.push(cityStateZip);
      }
    }

    if (addressParts.length > 0) {
      parts.push(''); // Add blank line
      parts.push('**Address:**');
      parts.push(...addressParts);
    }

    // Add phone and fax with pipe separator if both present
    const contactParts = [];
    if (providerBillingInfo.phone) {
      contactParts.push(`**Phone:** ${providerBillingInfo.phone}`);
    }
    if (providerBillingInfo.fax_number) {
      contactParts.push(`**Fax:** ${providerBillingInfo.fax_number}`);
    }

    if (contactParts.length > 0) {
      parts.push(''); // Add blank line
      parts.push(contactParts.join(' | '));
    }

    // Add a separator line
    if (parts.length > 0) {
      parts.push('');
      parts.push('---');
      parts.push('');
    }

    return parts.join('\n');
  }, [providerBillingInfo]);

  // Get the current letterhead content or use default
  const currentLetterhead = useMemo(() => {
    // Safely access document_preferences with type assertion
    const prefsWithDoc = preferences as UserPreferencesWithDocument | undefined;
    const savedLetterhead = prefsWithDoc?.document_preferences?.letterhead_md_string;
    if (savedLetterhead) {
      return savedLetterhead;
    }
    // If no saved letterhead and we have provider info, use the default
    return generateDefaultLetterhead();
  }, [preferences, generateDefaultLetterhead]);

  // Create BlockNote editor instance with ref pattern
  const editor = useMemo(() => {
    const newEditor = BlockNoteEditor.create({
      initialContent: undefined,
    });
    
    // Store in ref for stable reference
    editorRef.current = newEditor;
    
    return newEditor;
  }, []); // Empty deps is fine with ref pattern

  // Initialize editor content when preferences load
  useEffect(() => {
    const initializeEditor = async () => {
      if (!isInitialized && editor && !isLoadingPreferences) {
        try {
          const letterheadContent = currentLetterhead || '';
          const blocks = await editor.tryParseMarkdownToBlocks(letterheadContent);
          editor.replaceBlocks(editor.document, blocks);
          setLocalContent(letterheadContent);
          setIsInitialized(true);
        } catch (error) {
          console.error('Failed to initialize editor:', error);
          // Fallback to empty content if parsing fails
          editor.replaceBlocks(editor.document, []);
          setLocalContent('');
          setIsInitialized(true);
        }
      }
    };

    initializeEditor();
  }, [editor, currentLetterhead, isLoadingPreferences, isInitialized]);

  // Create debounced markdown conversion to prevent memory leaks
  const debouncedMarkdownConversion = useMemo(
    () =>
      debounce(async (editor: BlockNoteEditor, currentLetterhead: string | undefined) => {
        try {
          const blocks = editor.document;
          const markdown = await editor.blocksToMarkdownLossy(blocks);
          setLocalContent(markdown);
          setHasChanges(markdown !== currentLetterhead);
        } catch (error) {
          console.error('Failed to convert blocks to markdown:', error);
          // Defensive handling - don't crash the form
          setLocalContent('');
        }
      }, 300),
    [],
  );

  // Handle editor content changes with debouncing
  const handleEditorChange = useCallback(() => {
    if (!editor) return;
    debouncedMarkdownConversion(editor, currentLetterhead || '');
  }, [editor, currentLetterhead, debouncedMarkdownConversion]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedMarkdownConversion.cancel();
    };
  }, [debouncedMarkdownConversion]);

  // Save function
  const handleSave = async () => {
    try {
      await updateUserPreferences({
        updateUserPreferencesRequest: {
          document_preferences: {
            letterhead_md_string: localContent,
          },
        },
      }).unwrap();

      setHasChanges(false);
      // Call the success callback which will show the notification in UserView
      onSuccessfulFormSubmission?.();
    } catch (error) {
      console.error('Failed to save document preferences:', error);
      enqueueSnackbar('Failed to save document preferences', { variant: 'error' });
    }
  };

  // Cancel function - reset to original content with defensive error handling
  const handleCancel = async () => {
    if (editor) {
      try {
        const letterheadToReset = currentLetterhead || '';
        const blocks = await editor.tryParseMarkdownToBlocks(letterheadToReset);
        editor.replaceBlocks(editor.document, blocks);
        setLocalContent(letterheadToReset);
        setHasChanges(false);
      } catch (error) {
        console.error('Failed to reset editor:', error);
        // Fallback: try to set empty content if reset fails
        try {
          editor.replaceBlocks(editor.document, []);
          setLocalContent('');
          setHasChanges(false);
          enqueueSnackbar('Editor reset to empty state', { variant: 'info' });
        } catch (fallbackError) {
          console.error('Failed to reset editor completely:', fallbackError);
          enqueueSnackbar('Unable to reset editor. Please refresh the page.', { variant: 'error' });
        }
      }
    }
  };

  // Preview function - generate PDF with sample content
  const handlePreview = async () => {
    const sampleContent = `

## Sample Document

This is a preview of how your letterhead will appear in generated documents.

**Patient:** John Doe  
**Date:** ${new Date().toLocaleDateString()}

### Assessment

This is sample content to demonstrate how your custom letterhead will look when included in medical documents. Your letterhead appears at the top of the document and provides professional branding for all generated reports.

### Plan

1. Review letterhead formatting
2. Make any necessary adjustments
3. Save changes when satisfied

*This is a preview document generated to show letterhead formatting.*

[SIGNATURE]`;

    try {
      const response = await generatePdf({
        generatePdfRequest: {
          markdown: sampleContent,
          document_title: 'Letterhead Preview',
          patient_id: 'preview-patient',
        },
      }).unwrap();

      setPdfData({
        pdf_data: response.pdf_data,
        filename: 'letterhead_preview.pdf'
      });
      setShowPdfModal(true);
    } catch (error) {
      console.error('Failed to generate preview PDF:', error);
      enqueueSnackbar('Failed to generate preview. Please try again.', { variant: 'error' });
    }
  };

  if (isLoadingPreferences) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Check if we're showing default content
  const prefsWithDoc = preferences as UserPreferencesWithDocument | undefined;
  const isUsingDefault =
    !prefsWithDoc?.document_preferences?.letterhead_md_string && providerBillingInfo;

  return (
    <Box>
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Custom Letterhead
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a custom letterhead that will appear at the top of your generated documents. You
            can include your practice name, address, contact information, and any other details you
            want to appear on official documents.
          </Typography>
          {isUsingDefault && (
            <Alert severity="info" sx={{ mb: 2 }}>
              We&apos;ve pre-filled your letterhead with information from your provider settings.
              Feel free to customize it as needed.
            </Alert>
          )}
        </Box>

        <Paper
          variant="outlined"
          sx={{
            minHeight: 300,
            maxHeight: 500,
            overflow: 'auto',
            '& .bn-container': {
              borderRadius: 0,
              minHeight: 300,
            },
            '& .bn-editor': {
              padding: 2,
            },
          }}
        >
          <BlockNoteView
            editor={editor}
            onChange={handleEditorChange}
            theme="light"
            formattingToolbar={true}
            editable={!isSaving}
          />
        </Paper>

        {hasChanges && (
          <Alert severity="info" sx={{ mt: 1 }}>
            You have unsaved changes to your letterhead.
          </Alert>
        )}

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Tooltip 
            title={hasChanges ? "Save changes before previewing" : "Preview how your letterhead will appear in documents"}
            arrow
          >
            <span>
              <Button
                variant="outlined"
                startIcon={isGeneratingPdf ? <CircularProgress size={16} /> : <PreviewIcon />}
                onClick={handlePreview}
                disabled={isGeneratingPdf || hasChanges}
              >
                {isGeneratingPdf ? 'Generating...' : 'Preview PDF'}
              </Button>
            </span>
          </Tooltip>
          {hasChanges && (
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
          )}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="caption" color="text.secondary">
            Tip: Your letterhead will be converted to PDF format when generating documents. Keep
            formatting simple for best results.
          </Typography>
        </Box>
      </Stack>

      <PdfPreviewModal
        open={showPdfModal}
        onClose={() => setShowPdfModal(false)}
        pdfData={pdfData}
      />
    </Box>
  );
};

export default DocumentPreferencesForm;
